import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ReviewPeriodChangeStatus } from '../models/review-period-change-status.model';
import { ReviewPeriodForEdit } from '../models/review-period-for-edit.model';
import { ReviewPeriod } from '../models/review-period.model';
import { DataService } from './data.service';
import { StateService } from './state.service';
import { Result, ResultModel } from '../models/general/result.model';
import { Search } from '../models/search.model';
import { PagedListResult } from '../models/paged-list-result.model';
import { IdName } from '../models/general/id-name.model';
import { EmployeeReviews } from '../models/employee-reviews.model';
import { DateHelper } from '../shared/helpers';
import { ReviewType } from '../models/review-type.model';
import { ReviewTypeEnum } from '../enums/review-type.enum';
import { ReviewPeriodStatusEnum } from '../enums/review-period-status.enum';
import { IdTranslateKey } from '../models/general/id-translate-key.model';
import { AssessmentTypeEnum } from '../enums/assessment-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { ReviewPeriodStatus } from '../models/review-period-status.model';
import { AssessmentType } from '../models/assessment-type.model';

@Injectable({
    providedIn: 'root',    
})
export class ReviewPeriodService extends DataService {
    
    constructor(http: HttpClient, stateService: StateService, private translate: TranslateService) {
        super(http, stateService);
    }

    private reviewTypes: IdTranslateKey[] = [
        {id: ReviewTypeEnum.TER, translateKey: 'REVIEW_TYPES.PANORAMA_AURA'},
        {id: ReviewTypeEnum.PersonalityTest, translateKey: 'REVIEW_TYPES.SILUETT'},
        {id: ReviewTypeEnum.MilioStressMap, translateKey: 'REVIEW_TYPES.MILIO_STRESS_MAP'},
        {id: ReviewTypeEnum.MilioTeamDysfunction, translateKey: 'REVIEW_TYPES.MILIO_TEAM_DYSFUNCTION'},
    ];
    
    private reviewPeriodStatusList: IdTranslateKey[] = [
        {id: ReviewPeriodStatusEnum.New, translateKey: 'REVIEW_PERIOD_STATUS.PREPARATION'},
        {id: ReviewPeriodStatusEnum.Open, translateKey: 'REVIEW_PERIOD_STATUS.OPEN'},
        {id: ReviewPeriodStatusEnum.Closed, translateKey: 'REVIEW_PERIOD_STATUS.CLOSED'},
    ];

    private assessmentTypeList: IdTranslateKey[] = [    //AssessmentType
        {id: 0, translateKey: 'ASSESSMENT_TYPE.AUTO_DETECT'},
        {id: AssessmentTypeEnum.Self, translateKey: 'ASSESSMENT_TYPE.SELF'},
        {id: AssessmentTypeEnum.Leader, translateKey: 'ASSESSMENT_TYPE.LEADER'},
        {id: AssessmentTypeEnum.Peer, translateKey: 'ASSESSMENT_TYPE.PEER'},
        {id: AssessmentTypeEnum.Subordinate, translateKey: 'ASSESSMENT_TYPE.SUBORDINATE'},
    ];

    private assessmentEmployeeTypeList: IdTranslateKey[] = [      //AssessmentType
        {id: AssessmentTypeEnum.Self, translateKey: 'ASSESSMENT_EMPLOYEE_TYPE.SELF'},
        {id: AssessmentTypeEnum.Leader, translateKey: 'ASSESSMENT_EMPLOYEE_TYPE.SUBORDINATE'},
        {id: AssessmentTypeEnum.Peer, translateKey: 'ASSESSMENT_EMPLOYEE_TYPE.PEER'},
        {id: AssessmentTypeEnum.Subordinate, translateKey: 'ASSESSMENT_EMPLOYEE_TYPE.LEADER'},
    ];

    public getName(rpType: ReviewTypeEnum): string {
        const item = this.reviewTypes.find(x => x.id === rpType as number);
        if (item) {
            return this.translate.instant(item.translateKey);
        }
        return '';
    }

    public getAssessmentEmployeeTypeName(assessmentType: AssessmentTypeEnum): string {
      const item = this.assessmentEmployeeTypeList.find(x => x.id === assessmentType as number);
      return item ? this.translate.instant(item.translateKey) : '';
    }

    public getStatusName(status: ReviewPeriodStatusEnum): string {
      const item = this.reviewPeriodStatusList.find(x => x.id === status as number);
      return item ? this.translate.instant(item.translateKey) : '';
    }

    public getDisplayText(rp: ReviewPeriod): string {
        if (!rp) return '';
        if (!rp.id) return rp.name;

        const typeName = this.getName(rp.reviewType);
        const rpName = rp.name ? rp.name : '';

        return `${typeName} - ${rpName}`;
    }

    public getReviewTypes(): IdTranslateKey[] {
        return this.reviewTypes;
    }

    public getReviewPeriodStatusList(): IdTranslateKey[] {
        return this.reviewPeriodStatusList;
    }

    public getAssessmentTypeList(): IdTranslateKey[] {
        return this.assessmentTypeList;
    }
    
    getReviewPeriodList(userId?: string): Observable<ReviewPeriod[]> {
        const url = userId ? `/api/review-period/list/${userId}` : `/api/review-period/list`;
        return this.post<ReviewPeriod[]>(url, null).pipe(
            map(x => x.map(y => {
                // if (y.startDate)
                //     y.startDate = Temporal.PlainDate.from(y.startDate.toString());

                // if (y.endDate)
                //     y.endDate = Temporal.PlainDate.from(y.endDate.toString());

                if (y.createdOn)
                    y.createdOn = new Date(y.createdOn.toString());

                // if (y.panoramaAuraStartDate)
                //     y.panoramaAuraStartDate = Temporal.PlainDate.from(y.panoramaAuraStartDate.toString());

                // if (y.panoramaAuraEndDate)
                //     y.panoramaAuraEndDate = Temporal.PlainDate.from(y.panoramaAuraEndDate.toString());

                // if (y.goalsStartDate)
                //     y.goalsStartDate = Temporal.PlainDate.from(y.goalsStartDate.toString());

                // if (y.goalsEndDate)
                //     y.goalsEndDate = Temporal.PlainDate.from(y.goalsEndDate.toString());
                    
                return y;
            }))
        );
    }

    getList = (options?: Search): Observable<PagedListResult<ReviewPeriod>> => {
        if (!options) options = {search: ''} as Search;

        return this.post<PagedListResult<ReviewPeriod>>(`/api/review-period/list-search`, options, {headers: {'X-Skip-Interceptor':'1'}});
    }

    getListAsIdName = (options?: Search): Observable<PagedListResult<IdName>> => {
        return this.getList(options).pipe(
            map(x => {
                let pList = {
                    pageCount: x.pageCount,
                    page: x.page,
                    pageSize: x.pageSize,
                    total: x.total,
                    list: x.list.map(rp => {
                        let data = {
                            id: rp.id,
                            name: rp.name,
                            description: `${DateHelper.formatPlainDateShort(rp.startDate)} - ${DateHelper.formatPlainDateShort(rp.endDate)}`
                        } as IdName;
                        return data;
                    }), 
                } as PagedListResult<IdName>;                
                return pList;
            })
        );
    }    

    getListNotClosed(userId: string): Observable<ReviewPeriod[]> {
        const url = `/api/review-period/list-not-closed/${userId}`;
        return this.get<ReviewPeriod[]>(url);
    }

    getForEdit(reviewPeriodId?: number): Observable<ReviewPeriodForEdit> {
        const url = reviewPeriodId ? `/api/review-period/get-for-edit/${reviewPeriodId}` : `/api/review-period/get-for-edit`;
        return this.get<ReviewPeriodForEdit>(url);
    }

    getReviewPeriodListForPanoramaAndAura(): Observable<ReviewPeriod[]> {
        return this.get<ReviewPeriod[]>('/api/review-period/list-panorama-and-aura');
    }

    getEmployeeReviews(): Observable<EmployeeReviews> {
        return this.get<EmployeeReviews>('/api/review-period/employee-reviews');
    }

    add(model: ReviewPeriod): Observable<Result<ReviewPeriod>> {
        let data = {
            ...model,
            startDate: DateHelper.toPlainDate(model.startDate)?.toString(),
            endDate: DateHelper.toPlainDate(model.endDate)?.toString(),
            panoramaAuraStartDate: DateHelper.toPlainDate(model.panoramaAuraStartDate)?.toString(),
            panoramaAuraEndDate: DateHelper.toPlainDate(model.panoramaAuraEndDate)?.toString(),
            goalsStartDate: DateHelper.toPlainDate(model.goalsStartDate)?.toString(),
            goalsEndDate: DateHelper.toPlainDate(model.goalsEndDate)?.toString()
        }

        return this.put<Result<ReviewPeriod>>('/api/review-period/add', data);
    }

    update(model: ReviewPeriod): Observable<void> {
        let data = {
            ...model,
            startDate: DateHelper.toPlainDate(model.startDate)?.toString(),
            endDate: DateHelper.toPlainDate(model.endDate)?.toString(),
            panoramaAuraStartDate: DateHelper.toPlainDate(model.panoramaAuraStartDate)?.toString(),
            panoramaAuraEndDate: DateHelper.toPlainDate(model.panoramaAuraEndDate)?.toString(),
            goalsStartDate: DateHelper.toPlainDate(model.goalsStartDate)?.toString(),
            goalsEndDate: DateHelper.toPlainDate(model.goalsEndDate)?.toString()
        };
        
        return this.post<void>('/api/review-period/update', data);
    }

    deleteItem(id: number): Observable<ResultModel> {
        const url = `/api/review-period/delete/${id}`;
        return this.delete<ResultModel>(url);
    }

    changeStatus(model: ReviewPeriodChangeStatus): Observable<ResultModel> {
        return this.post<ResultModel>(`/api/review-period/change-status`, model);
    }
        
    isReviewExists(reviewPeriodId: number): Observable<boolean> {
        return this.get<boolean>(`/api/review-period/is-review-exists/${reviewPeriodId}`);
    }

    // generateSettings(reviewType: ReviewTypeEnum): Observable<ReviewPeriodQuestionSettings[]> {
    //     const url = `/api/review-period/generate-settings/${reviewType}`;
    //     return this.get<ReviewPeriodQuestionSettings[]>(url);
    // }
}
